import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Input, Select, Table, Typography } from 'antd';
import { API } from '@/services/api.service';

const { Text } = Typography;

/**
 * Who a publication goes to besides the platform itself: recipients outside
 * the platform (files attached), HLB people copied on every mail, and whether
 * the client's own users are told. Shared by the pack settings and the
 * publish dialog, which saves the same choice on the pack.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Array} props.recipients - [{ email, name, files }]
 * @param {Function} props.onRecipientsChange - Called with the new list
 * @param {Array} props.copies - HLB addresses in cc
 * @param {Function} props.onCopiesChange - Called with the new list
 * @param {boolean} props.notifyClientUsers - Whether the client's users are emailed
 * @param {Function} props.onNotifyChange - Called with the new value
 * @param {Array} props.fileOptions - [{ value, label }] pack file titles
 * @returns {JSX.Element} The recipient fields
 */
export default function RecipientsFields({
  companyId,
  recipients,
  onRecipientsChange,
  copies,
  onCopiesChange,
  notifyClientUsers,
  onNotifyChange,
  fileOptions,
}) {
  const { t } = useTranslation('clientReports');

  const { data: candidatesData } = useQuery({
    queryKey: ['client-reports-copy-candidates', companyId],
    queryFn: () => API.clientReports.getCopyCandidates(companyId),
    staleTime: 5 * 60 * 1000,
  });
  const candidates = candidatesData?.data || [];
  // A saved address that is no longer an admin's still shows as itself.
  const copyOptions = [
    ...candidates.map((c) => ({
      value: c.email,
      label: c.name ? `${c.name} <${c.email}>` : c.email,
    })),
    ...copies
      .filter((email) => !candidates.some((c) => c.email === email))
      .map((email) => ({ value: email, label: email })),
  ];

  const updateRecipient = (index, patch) =>
    onRecipientsChange(
      recipients.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );

  const recipientColumns = [
    {
      title: t('pack.recipients.email'),
      key: 'email',
      width: 260,
      render: (_, r, i) => (
        <Input
          type="email"
          value={r.email}
          placeholder="ime@firma.rs"
          onChange={(e) => updateRecipient(i, { email: e.target.value })}
        />
      ),
    },
    {
      title: t('pack.recipients.name'),
      key: 'name',
      width: 200,
      render: (_, r, i) => (
        <Input
          value={r.name}
          onChange={(e) => updateRecipient(i, { name: e.target.value })}
        />
      ),
    },
    {
      title: t('pack.recipients.files'),
      key: 'files',
      render: (_, r, i) => (
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 260 }}
          value={r.files || []}
          placeholder={t('pack.recipients.allFiles')}
          options={fileOptions}
          onChange={(files) => updateRecipient(i, { files })}
        />
      ),
    },
    {
      key: 'actions',
      width: 50,
      render: (_, __, i) => (
        <Button
          size="small"
          shape="circle"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onRecipientsChange(recipients.filter((_, k) => k !== i))}
        />
      ),
    },
  ];

  return (
    <div>
      <Checkbox
        checked={notifyClientUsers}
        onChange={(e) => onNotifyChange(e.target.checked)}
      >
        {t('pack.notifyClientUsers')}
      </Checkbox>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
        {t('pack.notifyClientUsersHint')}
      </div>

      <div style={{ marginBottom: 6 }}>
        <Text strong>{t('pack.recipients.title')}</Text>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          {t('pack.recipients.hint')}
        </div>
      </div>
      {recipients.length > 0 && (
        <Table
          scroll={{ x: 'max-content' }}
          rowKey={(_, i) => i}
          size="small"
          pagination={false}
          columns={recipientColumns}
          dataSource={recipients}
        />
      )}
      <Button
        shape="round"
        icon={<PlusOutlined />}
        style={{ marginTop: 8 }}
        onClick={() =>
          onRecipientsChange([...recipients, { email: '', name: '', files: [] }])
        }
      >
        {t('pack.recipients.add')}
      </Button>

      <div style={{ marginTop: 16, marginBottom: 6 }}>
        <Text strong>{t('pack.copies.title')}</Text>
        <div style={{ fontSize: 12, opacity: 0.7 }}>{t('pack.copies.hint')}</div>
      </div>
      <Select
        mode="tags"
        allowClear
        style={{ width: '100%' }}
        value={copies}
        placeholder={t('pack.copies.placeholder')}
        options={copyOptions}
        optionFilterProp="label"
        tokenSeparators={[',', ';', ' ']}
        onChange={(values) =>
          onCopiesChange([
            ...new Set(values.map((v) => String(v).trim().toLowerCase()).filter(Boolean)),
          ])
        }
      />
    </div>
  );
}
